import React, { useContext, useEffect, useRef } from 'react';
import { useEventListener, useKeyPress, useDebouncedCallback, useAdsData, } from '@24i/player-utils-react';
import { millisecondsToSeconds } from '@24i/player-utils';
import { PlayerUIContext, relativeSeek, relativeSeekCompleted } from '@24i/player-ui';
import { useTheme } from '@24i/nxg-sdk-higgs/src/hooks/useTheme';
import { Button } from '../Button/styled';
import { StyledIcon as Icon, IconName } from '../Icons';
const DEFAULT_SEEK_DELAY = 500;
const HOLD_TIMER = 200;
export var Sign;
(function (Sign) {
    Sign[Sign["Positive"] = 0] = "Positive";
    Sign[Sign["Negative"] = 1] = "Negative";
})(Sign || (Sign = {}));
const EDGE_TOLERANCE = 500;
function isAtEdge(playerInstance, sign) {
    if (!playerInstance)
        return true;
    const { seekableRange } = playerInstance;
    if (!seekableRange)
        return true;
    if (sign === Sign.Positive) {
        if (playerInstance.live)
            return playerInstance.isOnLiveEdge();
        return playerInstance.currentTime >= seekableRange.end - EDGE_TOLERANCE;
    }
    return playerInstance.currentTime <= seekableRange.start + EDGE_TOLERANCE;
}
export const SeekRelativeButton = ({ playerInstance, amount, sign, color, disabledColor, backgroundColor, className, customIcon, styles, disabled = false, }) => {
    var _a, _b;
    const { state: { relativeSeekValue }, dispatch, } = useContext(PlayerUIContext);
    const leftPress = useKeyPress('ArrowLeft');
    const rightPress = useKeyPress('ArrowRight');
    const button = useRef(null);
    const multiplierInterval = useRef(null);
    const { isSeekingBackwardDisabled, isSeekingForwardDisabled } = useAdsData(playerInstance);
    const isSeekingDisabled = sign === Sign.Positive ? isSeekingForwardDisabled : isSeekingBackwardDisabled;
    const { theme } = useTheme();
    const finalColor = color || ((_a = theme === null || theme === void 0 ? void 0 : theme.color) === null || _a === void 0 ? void 0 : _a.contrast1) || 'white';
    const finalDisabledColor = disabledColor || ((_b = theme === null || theme === void 0 ? void 0 : theme.color) === null || _b === void 0 ? void 0 : _b.contrast4) || 'gray';
    const finalDisabled = disabled || isSeekingDisabled || isAtEdge(playerInstance, sign);
    const iconColor = !finalDisabled ? finalColor : finalDisabledColor;
    const performSeek = () => {
        if (!relativeSeekValue)
            return;
        if (sign === Sign.Negative) {
            if (relativeSeekValue < 0) {
                playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.backward(Math.abs(relativeSeekValue));
                dispatch(relativeSeekCompleted());
            }
        }
        else if (relativeSeekValue > 0) {
            playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.forward(Math.abs(relativeSeekValue));
            dispatch(relativeSeekCompleted());
        }
    };
    const [debouncedSeek] = useDebouncedCallback(performSeek, DEFAULT_SEEK_DELAY);
    useEffect(() => {
        debouncedSeek();
    }, [relativeSeekValue]);
    const onKeyPress = (event) => {
        event.preventDefault();
    };
    useEventListener('keydown', onKeyPress, button.current);
    useEventListener('keyup', onKeyPress, button.current);
    const onClickHandler = (event) => {
        if (finalDisabled)
            return;
        dispatch(relativeSeek(sign === Sign.Negative ? -amount : amount));
        if (event && event.stopPropagation)
            event.stopPropagation();
    };
    const handleAccumulativePress = () => {
        if (finalDisabled)
            return;
        clearInterval(multiplierInterval.current);
        onClickHandler(null);
        multiplierInterval.current = setInterval(onClickHandler, HOLD_TIMER);
    };
    const handleAccumulativeRelease = () => {
        clearInterval(multiplierInterval.current);
        multiplierInterval.current = null;
    };
    useEffect(() => {
        if (sign !== Sign.Negative)
            return;
        if (leftPress && multiplierInterval.current === null) {
            handleAccumulativePress();
        }
        else if (!leftPress && multiplierInterval.current !== null) {
            handleAccumulativeRelease();
        }
    }, [leftPress]);
    useEffect(() => {
        if (sign !== Sign.Positive)
            return;
        if (rightPress && multiplierInterval.current === null) {
            handleAccumulativePress();
        }
        else if (!rightPress && multiplierInterval.current !== null) {
            handleAccumulativeRelease();
        }
    }, [rightPress]);
    const namingBySign = sign === Sign.Negative ? 'rewind' : 'forward';
    const iconName = `${namingBySign}_${millisecondsToSeconds(amount)}`;
    const stopPropagation = (event) => {
        event.stopPropagation();
        event.preventDefault();
    };
    return (React.createElement(Button, { ref: button, style: styles.button, "data-test": "SeekRelativeButton-Button", testID: `${namingBySign}_button`, className: `${className} SeekRelativeButton-Button`, onClick: finalDisabled ? stopPropagation : onClickHandler, onDoubleClick: stopPropagation, backgroundColor, finalDisabled }, customIcon(IconName[iconName], iconColor, backgroundColor, styles)));
};
const RenderDefaultIcon = (buttonIconName, color, backgroundColor, styles) => {
    return (React.createElement(Icon, { name: buttonIconName, style: styles.icon, "data-test": "SeekRelativeButton-Icon", className: "SeekRelativeButton-Icon", color, backgroundColor }));
};
SeekRelativeButton.defaultProps = {
    className: '',
    color: 'white',
    backgroundColor: 'transparent',
    amount: 10000,
    customIcon: RenderDefaultIcon,
    styles: {
        button: null,
        icon: null,
    },
};
//# sourceMappingURL=index.js.map